import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { RdsModule } from '@rabobank/rds-angular';
import { filter, Subject, takeUntil } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { RbacService } from '../../services/rbac.service';

@Component({
  selector: 'app-header',
  imports: [RdsModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private authService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private rbacService = inject(RbacService);
  private router = inject(Router);

  private readonly _destroying$ = new Subject<void>();
  activeTab!: string;
  isBankerIQEnabled: boolean = environment.featureToggles.enableBankerIQ;
  isBankerIQUser: boolean = false;
  isBusinessInputUser: boolean = false;
  isLoggedIn: boolean = false;
  isValueChainViewerUser: boolean = false;

  userName?: string = '';

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngOnInit(): void {
    this.getActiveTab();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        const allAccounts = this.authService.instance.getAllAccounts();
        const activeAccount = this.authService.instance.getActiveAccount();
        this.isLoggedIn = allAccounts.length > 0;

        if (!activeAccount && this.isLoggedIn) {
          this.authService.instance.setActiveAccount(allAccounts[0]);
        }

        this.userName = this.authService.instance.getActiveAccount()?.name;
      });
    this.rbacService.getUserAccess().subscribe({
      next: (userAccess) => {
        this.isBusinessInputUser = userAccess.businessInputAuthorizationStatus;
        this.isValueChainViewerUser =
          userAccess.valueChainViewerAuthorizationStatus;
        this.isBankerIQUser = userAccess.bankerIQAuthorizationStatus;
      },
      error: () => {
        this.isBusinessInputUser = false;
        this.isValueChainViewerUser = false;
        this.isBankerIQUser = false;
      },
    });
  }

  getActiveTab(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setActiveMenu(event.urlAfterRedirects);
      }
    });
  }

  goToBankerIQModule(): void {
    this.router.navigate(['/banker-iq/0/edit']);
  }

  goToBusinessInputModule(): void {
    this.router.navigate(['/business-input/value-chains'], {
      queryParams: { activeNavTab: 'Value Chains' },
    });
  }

  goToVcvModule(): void {
    this.router.navigate(['/home']);
  }

  onLogin() {
    this.authService.loginPopup().subscribe({
      next: () => {
        this.router.navigate(['']);
      },
      error: (error) => console.log(error),
    });
  }

  onLogout() {
    this.authService.logoutRedirect({
      onRedirectNavigate: () => false,
    });
    this.router.navigate(['logout-success']);
  }

  setActiveMenu(url: string) {
    if (url.startsWith('/business-input')) {
      this.activeTab = 'Business Input';
    } else if (url.startsWith('/banker-iq')) {
      this.activeTab = 'BankerIQ';
    } else {
      this.activeTab = 'Viewer';
    }
  }
}
